/**
 * Filter Fields Mount
 *
 * The calling control for the shared filter components — @components/priceFilter, squareFeetFilter,
 * bedroomsFilter, bathroomsFilter, moveInDateFilter, housingTypeFilter. Those components only produce
 * their markup as a string (`getHtml`) and bind behavior to an element they are given; this file is
 * the one that composes those strings with the modal's layout (grouping + dividers, in the
 * grouping/order shown in Figma nodes 5332:164900 / 7223:128574 / 7248:128652), turns the result
 * into DOM by inserting it into the modal body, and then hands each component's element to its
 * constructor. Each component exposes `getObservable()`; nothing here subscribes to them — the
 * caller (plugins/filtersModal.plugin.js#bindFilterDispatch) does, using the instances this
 * function returns, to dispatch each one's settled value into the Redux store.
 *
 * @module nativeInteractiveSiteMap/components/filterFields.dom
 */

import PriceFilter from '@components/priceFilter';
import SquareFeetFilter from '@components/squareFeetFilter';
import BedroomsFilter from '@components/bedroomsFilter';
import BathroomsFilter from '@components/bathroomsFilter';
import MoveInDateFilter from '@components/moveInDateFilter';
import HousingTypeFilter from '@components/housingTypeFilter';

const DIVIDER_HTML = '<div class="nhs-c-native-interactive-site-map__filters-modal-divider"></div>';

function createFilterFieldsHtml() {
  return `
    ${PriceFilter.getHtml()}
    ${DIVIDER_HTML}
    <div class="nhs-c-input__group">
      ${BedroomsFilter.getHtml()}
      ${BathroomsFilter.getHtml()}
    </div>
    ${DIVIDER_HTML}
    ${SquareFeetFilter.getHtml()}
    ${DIVIDER_HTML}
    <div class="nhs-c-native-interactive-site-map__filters-modal-row">
      ${MoveInDateFilter.getHtml()}
      ${HousingTypeFilter.getHtml()}
    </div>
  `;
}

/**
 * @param {Element} container - The (empty) element the filter fields are rendered into.
 * @returns {Object} `filters`: each component instance keyed by filter (`{ getObservable, destroy }`),
 *   for callers that need to subscribe or clean up.
 */
export default function mountFilterFields(container) {
  container.insertAdjacentHTML('beforeend', createFilterFieldsHtml());

  return {
    price: new PriceFilter(container.querySelector(PriceFilter.getSelector())),
    squareFeet: new SquareFeetFilter(container.querySelector(SquareFeetFilter.getSelector())),
    bedrooms: new BedroomsFilter(container.querySelector(BedroomsFilter.getSelector())),
    bathrooms: new BathroomsFilter(container.querySelector(BathroomsFilter.getSelector())),
    moveInDate: new MoveInDateFilter(container.querySelector(MoveInDateFilter.getSelector())),
    housingType: new HousingTypeFilter(container.querySelector(HousingTypeFilter.getSelector())),
  };
}
